<template>
  <div id="raster-analysis">
    <!-- No farm in context — the plugin is launched from inside a farm. -->
    <div v-if="!farmId" class="ra-no-farm">
      <p>Open the Raster Analysis tool from one of your farms.</p>
      <router-link to="/">← Back to your farms</router-link>
    </div>

    <template v-else>
      <PluginHeader
        title="Raster Analysis"
        subtitle="Sample raster values onto a survey dataset"
        icon="fas fa-file-image"
      />

      <transition name="ra-fade">
        <div v-if="alert.show" class="ra-alert" :class="alert.level">
          {{ alert.message }}
        </div>
      </transition>

      <!-- Options panel -->
      <div class="ra-panel">
        <p class="ra-intro">
          Sample a raster dataset onto the points of a survey dataset —
          each point gets the raster value beneath it (or the mean of a
          small neighborhood).
        </p>

        <!-- Raster dataset -->
        <label class="ra-label">Raster dataset</label>
        <RasterDatasetPicker
          v-if="!rasterDataset"
          mode="raster"
          :farmId="farmId"
          @select="pickRaster"
        />
        <div v-else class="ra-chosen">
          <span>{{ rasterDataset.name }}</span>
          <i class="fas fa-times-circle ra-clear" @click="clearRaster"></i>
        </div>

        <!-- Point dataset -->
        <label class="ra-label">Point dataset</label>
        <RasterDatasetPicker
          v-if="!pointDataset"
          mode="point"
          :farmId="farmId"
          @select="pickPoint"
        />
        <div v-else class="ra-chosen">
          <span>{{ pointDataset.name }}</span>
          <i class="fas fa-times-circle ra-clear" @click="clearPoint"></i>
        </div>

        <!-- Neighborhood -->
        <label class="ra-label">
          Neighborhood
          <i
            class="fas fa-question-circle ra-help"
            v-b-tooltip.hover
            title="A square window centered on each point. A 3 m neighborhood averages the raster over a 3×3 m square; 'No neighborhood' reads the single pixel under the point."
          ></i>
        </label>
        <select v-model.number="neighborhoodSize" class="ra-select">
          <option
            v-for="opt in neighborhoodOptions"
            :key="opt.value"
            :value="opt.value"
          >{{ opt.text }}</option>
        </select>

        <!-- Analyze -->
        <button
          v-if="rasterDataset && pointDataset && !analyzing"
          class="ra-btn ra-btn-primary"
          @click="analyze"
        >Analyze raster</button>
        <button v-if="analyzing" class="ra-btn ra-btn-muted" disabled>
          <i class="fas fa-sync fa-spin"></i> Analyzing…
        </button>

        <!-- Results -->
        <div v-if="analyzedGeoJson" class="ra-results">
          <p class="ra-success">
            <i class="fas fa-check-circle"></i>
            {{ analyzedGeoJson.features.length }} points sampled.
          </p>
          <button class="ra-btn" @click="download">Download GeoJSON</button>
          <button
            v-if="!saving"
            class="ra-btn ra-btn-success"
            @click="saveToMyev"
          >Add to Every.Farm</button>
          <button v-else class="ra-btn ra-btn-muted" disabled>
            <i class="fas fa-sync fa-spin"></i> Adding…
          </button>
        </div>

        <button class="ra-btn ra-btn-link" @click="reset">Reset</button>
      </div>

      <!-- Map -->
      <div id="ra-map" class="ra-map"></div>
    </template>
  </div>
</template>

<script>
/* eslint-disable no-console */
import firebase from 'firebase/compat/app'
import { deriveHeaders } from '@/lib/deriveHeaders'
import geoblaze from 'geoblaze'
import proj4 from 'proj4'
import RasterDatasetPicker from './RasterDatasetPicker.vue'
import PluginHeader from '../_shared/PluginHeader.vue'

const epsg = require('epsg')
const turf = require('@turf/turf')
const fb = require('@/firebaseConfig.js')

// Public Mapbox token already used elsewhere in the app (FarmsMap.vue).
const MAPBOX_TOKEN = 'pk.eyJ1IjoibGVyZ3AiLCJhIjoiY2p4bmI1NzNzMGN0MTNjcGx4cjF4eDBtdSJ9.2C0FEHhNZ-jGd7jgIRTrEQ'

export default {
  name: 'RasterAnalysis',
  components: { RasterDatasetPicker, PluginHeader },
  data () {
    return {
      rasterDataset: null,
      pointDataset: null,
      neighborhoodSize: 3,
      neighborhoodOptions: [
        { value: 0, text: 'No neighborhood (single pixel)' },
        { value: 1, text: '1 meter' },
        { value: 3, text: '3 meter' },
        { value: 5, text: '5 meter' },
        { value: 10, text: '10 meter' },
        { value: 30, text: '30 meter' },
        { value: 50, text: '50 meter' }
      ],
      analyzing: false,
      analyzedGeoJson: null,
      saving: false,
      alert: { show: false, message: '', level: 'alert-info' }
    }
  },
  computed: {
    farmId () {
      return this.$store.state.farm && this.$store.state.farm.id
    },
    farmCenter () {
      const g = this.$store.state.farm && this.$store.state.farm.geolocation
      if (!g) return [-95, 40]
      // Firestore GeoPoint exposes latitude/longitude; legacy serialized
      // forms used _latitude/_longitude — support both.
      const lat = g.latitude != null ? g.latitude : g._latitude
      const lng = g.longitude != null ? g.longitude : g._longitude
      return [lng, lat]
    }
  },
  mounted () {
    if (this.farmId) this.initMap()
  },
  beforeDestroy () {
    if (this.map) {
      try { this.map.remove() } catch (e) { /* ignore */ }
    }
  },
  methods: {
    initMap () {
      mapboxgl.accessToken = MAPBOX_TOKEN // eslint-disable-line no-undef
      this.map = new mapboxgl.Map({ // eslint-disable-line no-undef
        container: 'ra-map',
        style: 'mapbox://styles/mapbox/satellite-v9',
        center: this.farmCenter,
        zoom: 15
      })
      this.map.addControl(new mapboxgl.NavigationControl()) // eslint-disable-line no-undef
    },
    flash (message, level) {
      this.alert = { show: true, message, level: level || 'alert-info' }
      clearTimeout(this._alertTimer)
      this._alertTimer = setTimeout(() => { this.alert.show = false }, 7000)
    },
    async storageUrl (path) {
      return firebase.storage().ref(path).getDownloadURL()
    },

    // --- raster dataset ---------------------------------------------
    async pickRaster (dataset) {
      this.rasterDataset = dataset
      try {
        await this.mapRaster()
      } catch (err) {
        this.flash('Could not display the raster: ' + (err.message || err), 'alert-warning')
      }
    },
    async mapRaster () {
      const m = this.map
      const file = this.rasterDataset.file || {}
      this.removeRasterLayer()
      const { xMin, xMax, yMin, yMax } = file
      const haveBounds = [xMin, xMax, yMin, yMax].every(v => typeof v === 'number')
      if (!haveBounds || !file.jpgPath) {
        // Overlay needs a preview image + bounds; analysis still works
        // from the GeoTIFF regardless.
        return
      }
      const url = await this.storageUrl(file.jpgPath)
      m.addSource('rasterSource', {
        type: 'image',
        url,
        coordinates: [
          [xMin, yMax], [xMax, yMax], [xMax, yMin], [xMin, yMin]
        ]
      })
      m.addLayer({
        id: 'rasterLayer',
        source: 'rasterSource',
        type: 'raster',
        paint: { 'raster-opacity': 0.85 }
      })
      m.fitBounds([[xMin, yMin], [xMax, yMax]], { animate: false })
    },
    removeRasterLayer () {
      const m = this.map
      if (!m) return
      if (m.getLayer('rasterLayer')) m.removeLayer('rasterLayer')
      if (m.getSource('rasterSource')) m.removeSource('rasterSource')
    },
    clearRaster () {
      this.rasterDataset = null
      this.removeRasterLayer()
    },

    // --- point dataset ----------------------------------------------
    async pickPoint (dataset) {
      this.pointDataset = dataset
      try {
        await this.mapPoints()
      } catch (err) {
        this.flash('Could not display the points: ' + (err.message || err), 'alert-warning')
      }
    },
    async mapPoints () {
      const m = this.map
      this.removePointLayer()
      const url = await this.storageUrl(this.pointDataset.file.geoJsonPath)
      m.addSource('pointSource', { type: 'geojson', data: url })
      m.addLayer({
        id: 'pointLayerOutline',
        type: 'circle',
        source: 'pointSource',
        paint: { 'circle-radius': 4, 'circle-color': '#ffffff' }
      })
      m.addLayer({
        id: 'pointLayer',
        type: 'circle',
        source: 'pointSource',
        paint: { 'circle-radius': 2.5, 'circle-color': '#e74c3c' }
      })
    },
    removePointLayer () {
      const m = this.map
      if (!m) return
      if (m.getLayer('pointLayer')) m.removeLayer('pointLayer')
      if (m.getLayer('pointLayerOutline')) m.removeLayer('pointLayerOutline')
      if (m.getSource('pointSource')) m.removeSource('pointSource')
    },
    clearPoint () {
      this.pointDataset = null
      this.removePointLayer()
    },

    // --- analysis ---------------------------------------------------
    async analyze () {
      this.analyzing = true
      this.analyzedGeoJson = null
      try {
        const rasterUrl = await this.storageUrl(this.rasterDataset.file.path)
        const raster = await geoblaze.load(rasterUrl)

        const pointsUrl = await this.storageUrl(this.pointDataset.file.geoJsonPath)
        const pointsGeoJson = await (await fetch(pointsUrl)).json()

        const out = { type: 'FeatureCollection', features: [] }
        pointsGeoJson.features.forEach(feature => {
          // Reproject the point into the raster's CRS when they differ.
          let featureCoordinates
          if (raster.projection === 4326) {
            featureCoordinates = feature.geometry.coordinates
          } else {
            featureCoordinates = proj4(
              epsg['EPSG:4326'],
              epsg['EPSG:' + raster.projection],
              feature.geometry.coordinates
            )
          }

          let analyzedData
          if (this.neighborhoodSize === 0) {
            analyzedData = geoblaze.identify(raster, featureCoordinates)
          } else {
            const radius = (this.neighborhoodSize / 2) / 1000 // km
            const circle = turf.circle(feature.geometry.coordinates, radius, {
              steps: 4, units: 'kilometers'
            })
            const bounds = turf.bbox(circle)
            if (raster.projection === 4326) {
              analyzedData = geoblaze.mean(raster, bounds)
            } else {
              const mins = proj4(epsg['EPSG:4326'], epsg['EPSG:' + raster.projection], [bounds[0], bounds[1]])
              const maxs = proj4(epsg['EPSG:4326'], epsg['EPSG:' + raster.projection], [bounds[2], bounds[3]])
              analyzedData = geoblaze.mean(raster, [mins[0], mins[1], maxs[0], maxs[1]])
            }
          }

          out.features.push({
            type: 'Feature',
            properties: Object.assign({}, analyzedData),
            geometry: feature.geometry
          })
        })

        this.analyzedGeoJson = out
        this.flash(`Analysis complete — ${out.features.length} points sampled.`, 'alert-success')
      } catch (err) {
        console.error('Raster analysis failed:', err)
        this.flash('Analysis failed: ' + (err.message || err), 'alert-danger')
      } finally {
        this.analyzing = false
      }
    },

    // --- outputs ----------------------------------------------------
    download () {
      const blob = new Blob([JSON.stringify(this.analyzedGeoJson)], {
        type: 'application/json;charset=utf-8'
      })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = 'raster-analysis.geojson'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(a.href)
    },
    async saveToMyev () {
      this.saving = true
      try {
        const farmId = this.farmId
        const path = `datasets/${farmId}/raster-analysis-${Date.now()}.geojson`
        await firebase.storage().ref(path).putString(
          JSON.stringify(this.analyzedGeoJson), 'raw',
          {
          contentType: 'application/json',
          // usageFinalize attributes bytes by customMetadata.userId —
          // without it, plugin-generated datasets aren't counted.
          customMetadata: { userId: (firebase.auth().currentUser && firebase.auth().currentUser.uid) || '' }
        }
        )
        const feats = this.analyzedGeoJson.features
        const headers = deriveHeaders({ features: feats })
        const uid = this.$store.state.currentUser && this.$store.state.currentUser.uid
        // Matches the dataset doc shape BulkUploadModal.createDatasetDocument writes.
        await fb.datasetsCollection.add({
          name: 'Raster Analysis — ' + this.pointDataset.name,
          description: `Values from "${this.rasterDataset.name}" sampled onto ` +
            `"${this.pointDataset.name}"` +
            (this.neighborhoodSize
              ? ` (${this.neighborhoodSize} m neighborhood mean).`
              : ' (single-pixel values).'),
          headers,
          farmId,
          createdOn: new Date(),
          createdBy: uid,
          updatedOn: new Date(),
          updatedBy: uid,
          file: {
            geoJsonPath: path,
            path,
            originalFileName: 'raster-analysis.geojson'
          },
          folderId: 'root',
          dynamic: false
        })
        this.flash('Saved to your farm as a new dataset.', 'alert-success')
      } catch (err) {
        console.error('Save failed:', err)
        this.flash(
          'Could not save: ' + (err.message || err) +
          ' — you can still download the GeoJSON and upload it manually.',
          'alert-danger'
        )
      } finally {
        this.saving = false
      }
    },
    reset () {
      this.removeRasterLayer()
      this.removePointLayer()
      this.rasterDataset = null
      this.pointDataset = null
      this.analyzedGeoJson = null
      if (this.map) {
        this.map.flyTo({ center: this.farmCenter, zoom: 15, animate: false })
      }
    }
  }
}
</script>

<style scoped>
#raster-analysis {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.ra-no-farm {
  padding: 60px 24px;
  text-align: center;
}
/* Floats below the shared 56px plugin header. */
.ra-alert {
  position: fixed;
  top: 68px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 101;
  max-width: 520px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}
.ra-alert.alert-info { background: #d1ecf1; color: #0c5460; }
.ra-alert.alert-success { background: #d4edda; color: #155724; }
.ra-alert.alert-warning { background: #fff3cd; color: #856404; }
.ra-alert.alert-danger { background: #f8d7da; color: #721c24; }
.ra-fade-enter-active, .ra-fade-leave-active { transition: opacity 0.3s; }
.ra-fade-enter, .ra-fade-leave-to { opacity: 0; }

.ra-panel {
  position: absolute;
  top: 56px;
  left: 0;
  width: 320px;
  height: calc(100% - 56px);
  overflow-y: auto;
  background: #fff;
  border-right: 1px solid #e3e6ea;
  padding: 16px 16px 48px;
  z-index: 1;
}
.ra-title { font-size: 1.15rem; margin: 0 0 6px; color: #2c3e50; }
.ra-title i { color: #066739; }
.ra-intro { font-size: 0.82rem; color: #6c757d; margin-bottom: 14px; }
.ra-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin: 14px 0 4px;
}
.ra-help { color: #b0b7bd; cursor: help; }
.ra-chosen {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: rgba(6, 103, 57, 0.10);
  border: 1px solid rgba(6, 103, 57, 0.30);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.88rem;
  color: #2c3e50;
}
.ra-clear { color: #95a5a6; cursor: pointer; }
.ra-clear:hover { color: #c0392b; }
.ra-select {
  width: 100%;
  font-size: 0.85rem;
  padding: 5px 6px;
  border: 1px solid #d6dbe0;
  border-radius: 4px;
}
.ra-btn {
  width: 100%;
  margin-top: 10px;
  padding: 8px 12px;
  border: 1px solid #d6dbe0;
  border-radius: 6px;
  background: #fff;
  font-size: 0.88rem;
  cursor: pointer;
}
.ra-btn:hover { background: #f5f7f8; }
.ra-btn-primary {
  background: #066739;
  border-color: #066739;
  color: #fff;
}
.ra-btn-primary:hover { background: #0a7d46; }
.ra-btn-success {
  background: #28a745;
  border-color: #28a745;
  color: #fff;
}
.ra-btn-success:hover { background: #2fbe4f; }
.ra-btn-muted { background: #f0f1f3; color: #95a5a6; cursor: default; }
.ra-btn-link {
  border: none;
  background: none;
  color: #95a5a6;
  font-size: 0.8rem;
}
.ra-results {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eef1f4;
}
.ra-success { font-size: 0.85rem; color: #155724; margin-bottom: 4px; }

.ra-map {
  position: absolute;
  top: 56px;
  left: 320px;
  width: calc(100% - 320px);
  height: calc(100% - 56px);
}
</style>
